import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Pill } from '../components/ui/Pill'
import { Avatar } from '../components/ui/Avatar'
import { EmptyState } from '../components/ui/EmptyState'
import { SectionLabel } from '../components/ui/SectionLabel'
import { useToast } from '../contexts/ToastContext'
import { useApprovals, useDecideApproval } from '../hooks/useFriends'
import type { ApprovalItemDto } from '../types/community'

/** Unified "Needs your yes" queue (COTC_FriendsApprovals R2/R4 / DESIGN §3.2). */
export function ApprovalsPage() {
  const { flash } = useToast()
  const qc = useQueryClient()
  const approvals = useApprovals()
  const decide = useDecideApproval()
  const items = approvals.data ?? []
  const pending = useRef(new Set<string>())
  const [rowState, setRowState] = useState<Record<string, 'pending' | 'error'>>({})
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  async function act(item: ApprovalItemDto, decision: 'approve' | 'decline') {
    const identity = `${item.kind}:${item.id}`
    if (pending.current.has(identity)) return
    pending.current.add(identity)
    setRowState((old) => ({ ...old, [identity]: 'pending' }))
    const key = ['community', 'approvals']
    try {
      await decide.mutateAsync({ kind: item.kind, id: item.id, decision })
      if (!mounted.current) return
      qc.setQueryData<ApprovalItemDto[]>(key, (old) => (old ?? []).filter((i) => i.id !== item.id || i.kind !== item.kind))
      flash(decision === 'approve' ? 'Request approved.' : 'Request declined.')
    } catch {
      if (mounted.current) setRowState((old) => ({ ...old, [identity]: 'error' }))
    } finally {
      pending.current.delete(identity)
    }
  }

  return (
    <div className="animate-rise px-5 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">Needs your yes</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">Needs your yes</h1>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-relaxed text-ink-muted">
        Nothing here happens without you — not a friend request, not a family joining one of your circles.
      </p>

      <Card className="mt-7" padding="none">
        <div className="flex items-center justify-between border-b border-line-row px-5 py-4">
          <SectionLabel>Waiting on you</SectionLabel>
          <span className="font-mono text-[11px] uppercase tracking-label text-ink-faint">{approvals.isLoading ? 'Loading' : approvals.isError ? 'Unavailable' : `${items.length} open`}</span>
        </div>

        {approvals.isLoading ? (
          <p className="px-5 py-6 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
        ) : approvals.isError ? (
          <div role="alert" className="px-5 py-5 text-[13px] text-ink-muted">
            <p>Requests could not be loaded.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => void approvals.refetch()}>Retry requests</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-3">
            <EmptyState title="All caught up." description="Nothing needs your yes right now." />
          </div>
        ) : (
          <ul className="divide-y divide-line-row">
            {items.map((item) => (
              <li key={`${item.kind}:${item.id}`} className="flex flex-wrap items-start gap-3 px-5 py-4 sm:gap-4" aria-busy={rowState[`${item.kind}:${item.id}`] === 'pending'}>
                <Avatar initials={item.initials || '?'} tintIndex={item.tintIndex} size={40} ringClass="border-paper-surface" />
                <div className="min-w-0 flex-1 basis-[180px]">
                  <p className="text-[14px] font-medium text-ink">{item.title}</p>
                  {item.detail && <p className="mt-0.5 text-[13px] text-ink-muted">{item.detail}</p>}
                  {item.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.tags.map((t) => <Pill key={t}>{t}</Pill>)}
                    </div>
                  )}
                  {rowState[`${item.kind}:${item.id}`] === 'error' && <p role="alert" className="mt-2 text-[13px] text-terra-deep">That decision did not go through. Try again.</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="approve" size="sm" disabled={rowState[`${item.kind}:${item.id}`] === 'pending'} onClick={() => act(item, 'approve')}>{rowState[`${item.kind}:${item.id}`] === 'pending' ? 'Sending…' : 'Approve'}</Button>
                  <Button variant="outline" size="sm" disabled={rowState[`${item.kind}:${item.id}`] === 'pending'} onClick={() => act(item, 'decline')}>Not this one</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
